import React from 'react';
import dayjs from 'dayjs';

import { UiComponent } from 'components';
import useModal from 'hooks/useModal';
import { IUser } from 'types';
import { formatPhoneNumber } from 'utils/format';
import Edit from '../Detail/Edit';
import UserMemo from '../Detail/UserMemo';
import useEdit from '../Detail/useEdit';
import useUserMemo from '../Detail/useUserMemo';
import useUserStatus from '../Detail/useUserStatus';
import useDetail from '../useDetail';
import { getUserStatus } from '../utils/status';
import * as M from 'styles/mobile';
import * as S from './style';

function Info() {
  const { user } = useDetail();
  if (!user) return null;

  return <InfoContent user={user} />;
}

function InfoContent({ user }: { user: IUser & { email?: string } }) {
  const { handleConfirm } = useModal();
  const { isOpenEdit, setIsOpenEdit, handleSave } = useEdit({ user });
  const {
    isOpenUserMemo,
    setIsOpenUserMemo,
    handleSave: handleSaveMemo,
  } = useUserMemo({ user });
  const { handleChagneStatus } = useUserStatus({ user });

  const isWithdraw = user.status === 'WITHDRAW';
  const isBlock = user.status === 'BLOCK';

  const handleStatus = () =>
    handleConfirm({
      confirmContent: isBlock
        ? `${user.name}님의 이용정지를 해제하시겠습니까?`
        : `${user.name}님을 이용정지 하시겠습니까?`,
      yesTitle: isBlock ? '해제' : '이용정지',
      noTitle: '취소',
      confirmNext: () => handleChagneStatus(isBlock ? 'NORMAL' : 'BLOCK'),
    });

  const rows: [string, string][] = [
    ['회원상태', getUserStatus(user.status)],
    ['가입일', dayjs(user.signUpDate).format('YYYY-MM-DD')],
    ['휴대폰번호', formatPhoneNumber(user.phone) || '-'],
    ['생년월일', user.birthDate || '-'],
    ['성별', user.gender === 'MALE' ? '남' : '여'],
    ['이메일', user.email || '-'],
    ['vms', user.vms || '-'],
    ['1365', user.id1365 || '-'],
  ];

  return (
    <div css={M.section}>
      <div css={M.card}>
        <div css={M.cardHeader}>
          {user.name}
          {!isWithdraw && (
            <button
              type="button"
              css={M.textButton}
              onClick={() => setIsOpenEdit(true)}
            >
              수정
            </button>
          )}
        </div>
        <dl css={M.infoList}>
          {rows.map(([label, value]) => (
            <div key={label} css={M.infoRow}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div css={M.card}>
        <div css={M.cardHeader}>
          메모
          {!isWithdraw && (
            <button
              type="button"
              css={M.textButton}
              onClick={() => setIsOpenUserMemo(true)}
            >
              편집
            </button>
          )}
        </div>
        {user.memo ? (
          <p css={S.memo}>{user.memo}</p>
        ) : (
          <p css={S.memoEmpty}>등록된 메모가 없습니다</p>
        )}
      </div>

      {!isWithdraw && (
        <UiComponent.Button
          variant="border"
          color="grey"
          css={S.statusButton}
          onClick={handleStatus}
        >
          {isBlock ? '이용정지 해제' : '이용정지'}
        </UiComponent.Button>
      )}

      {isOpenEdit && (
        <Edit
          user={user}
          onClose={() => setIsOpenEdit(false)}
          onSave={handleSave}
        />
      )}
      {isOpenUserMemo && (
        <UserMemo
          user={user}
          onClose={() => setIsOpenUserMemo(false)}
          onSave={handleSaveMemo}
        />
      )}
    </div>
  );
}

export default Info;
